import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import { getESGColor } from '@app/src/pages/ResourceCollection/Collections/RiskScreening/ProviderRisksPageRow'
import SustainabilityAreaIcon from '@app/src/pages/TopicalRisk/SustainabilityAreaIcon'
import { Box, Chip, Link, Paper, Stack, Switch, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { TopicCard } from './riskIndexCards'
import { applyTopicToggle, weightingFooter } from './riskIndexCards'
import RiskIndexRow from './RiskIndexRow'

interface RiskIndexTopicCardProps {
  card: TopicCard
  isEditMode: boolean
  onToggleIndex: (riskIndexId: number, isActivated: boolean) => void
  onHowThisWorks: () => void
}

const RiskIndexTopicCard: React.FC<RiskIndexTopicCardProps> = ({ card, isEditMode, onToggleIndex, onHowThisWorks }) => {
  const { formatMessage } = useIntl()

  const fallbackSource = card.countrySetting?.riskType.source ?? card.industrySetting?.riskType.source
  const title =
    card.title ?? (fallbackSource ? formatMessage({ id: `schemas.risk.riskIndexes.${fallbackSource}.label` }) : '')

  const handleToggleTopic = (isActivated: boolean) =>
    applyTopicToggle(card, isActivated).forEach(change => onToggleIndex(change.riskIndexId, change.isActivated))

  const footer = weightingFooter(card)
  const { countrySetting, industrySetting } = card

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        pt: 2,
        borderRadius: '8px',
        borderColor: 'divider',
        backgroundColor: card.isActive ? 'background.paper' : 'grey.50',
        minHeight: 256,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Avatar variant="circular" size={AvatarSize.Medium} sx={{ backgroundColor: getESGColor(card.area) }}>
          <SustainabilityAreaIcon area={card.area} fontSize="small" sx={{ color: 'grey.600' }} />
        </Avatar>

        <Typography variant="subtitle1" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
          {title}
        </Typography>

        {isEditMode ? (
          <Switch
            checked={card.isActive}
            onChange={(_, checked) => handleToggleTopic(checked)}
            inputProps={{ 'aria-label': title }}
          />
        ) : card.isActive ? (
          <Chip
            size="small"
            color="primary"
            label={formatMessage({
              id: 'riskScreeningConfig.cards.active',
            })}
          />
        ) : (
          <Typography color="text.disabled">
            {formatMessage({
              id: 'riskScreeningConfig.cards.inactive',
            })}
          </Typography>
        )}
      </Stack>

      <Stack gap={1}>
        {countrySetting && (
          <RiskIndexRow
            setting={countrySetting}
            isEditMode={isEditMode}
            isTopicActive={card.isActive}
            onToggle={isActivated => onToggleIndex(countrySetting.riskType.id, isActivated)}
          />
        )}
        {industrySetting && (
          <RiskIndexRow
            setting={industrySetting}
            isEditMode={isEditMode}
            isTopicActive={card.isActive}
            onToggle={isActivated => onToggleIndex(industrySetting.riskType.id, isActivated)}
          />
        )}
      </Stack>

      <Box sx={{ mt: 'auto', pt: 2 }}>
        <CardFooter footer={footer} onHowThisWorks={onHowThisWorks} />
      </Box>
    </Paper>
  )
}

const CardFooter: React.FC<{ footer: ReturnType<typeof weightingFooter>; onHowThisWorks: () => void }> = ({
  footer,
  onHowThisWorks,
}) => {
  const { formatMessage } = useIntl()

  if (footer.kind === 'both')
    return (
      <Typography variant="caption" color="text.secondary">
        {formatMessage(
          { id: 'riskScreeningConfig.cards.weighting' },
          { country: footer.countryWeight, industry: footer.industryWeight },
        )}{' '}
        <Link component="button" variant="caption" underline="always" onClick={onHowThisWorks}>
          {formatMessage({ id: 'riskScreeningConfig.cards.howThisWorks' })}
        </Link>
      </Typography>
    )

  const footerMessageId =
    footer.kind === 'countryOnly'
      ? 'riskScreeningConfig.cards.countryRiskOnly'
      : footer.kind === 'industryOnly'
        ? 'riskScreeningConfig.cards.industryRiskOnly'
        : 'riskScreeningConfig.cards.topicNotActivated'

  return (
    <Typography variant="caption" color="text.secondary">
      {formatMessage({ id: footerMessageId })}
    </Typography>
  )
}

export default RiskIndexTopicCard
